/**
 * EmailConnectionsPage - Admin dashboard for email connections
 *
 * Shows all OAuth-connected email accounts across companies,
 * sync status, files extracted, and allows triggering manual syncs.
 */

import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Mail,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  FileText,
  Loader2,
  Building2,
  Play,
  XCircle,
  TrendingUp,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useEmailConnectionsAdmin, EmailConnectionAdmin, SyncResult } from '../hooks/useEmailConnectionsAdmin';

type ThemeName = 'dashboard' | 'companies' | 'onboard' | 'analytics' | 'security';
type Theme = { from: string; to: string; glow: string; [key: string]: any };
type OutletContextType = {
  activeTheme: ThemeName;
  themes: Record<ThemeName, Theme>;
};

/** Display label for the mailbox provider. `outlook` is Microsoft-branded. */
const providerLabel = (type: EmailConnectionAdmin['type']): string =>
  type === 'outlook' ? 'Microsoft' : 'Gmail';

// Status badge component
const StatusBadge: React.FC<{ status: EmailConnectionAdmin['status'] }> = ({ status }) => {
  const statusConfig: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
    active: {
      color: 'bg-green-500/20 text-green-400 border-green-500/30',
      label: 'Active',
      icon: <CheckCircle size={12} />,
    },
    pending_auth: {
      color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      label: 'Pending Auth',
      icon: <Clock size={12} />,
    },
    auth_exchanging: {
      color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      label: 'Connecting',
      icon: <Loader2 size={12} className="animate-spin" />,
    },
    reauth_required: {
      color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      label: 'Reconnect Required',
      icon: <AlertCircle size={12} />,
    },
    admin_consent_required: {
      color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      label: 'Admin Approval Needed',
      icon: <ShieldAlert size={12} />,
    },
    auth_failed: {
      color: 'bg-red-500/20 text-red-400 border-red-500/30',
      label: 'Connection Failed',
      icon: <AlertCircle size={12} />,
    },
    paused: {
      color: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      label: 'Paused',
      icon: <Clock size={12} />,
    },
    error: {
      color: 'bg-red-500/20 text-red-400 border-red-500/30',
      label: 'Error',
      icon: <AlertCircle size={12} />,
    },
    revoked: {
      color: 'bg-gray-500/20 text-gray-500 border-gray-600/30',
      label: 'Revoked',
      icon: <XCircle size={12} />,
    },
    pending_invite: {
      color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      label: 'Invited',
      icon: <Mail size={12} />,
    },
  };

  const config = statusConfig[status] || statusConfig.error;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
      {config.icon}
      {config.label}
    </span>
  );
};

// Sync result badge
const SyncResultBadge: React.FC<{ result?: 'success' | 'partial' | 'failed' }> = ({ result }) => {
  if (!result) return <span className="text-xs text-gray-500">Never synced</span>;

  const config = {
    success: { color: 'text-green-400', label: 'Success' },
    partial: { color: 'text-yellow-400', label: 'Partial' },
    failed: { color: 'text-red-400', label: 'Failed' },
  };

  const c = config[result];
  return <span className={`text-xs ${c.color}`}>{c.label}</span>;
};

// Stat card component
const StatCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, icon, color }) => (
  <div className={`bg-gray-800/50 border border-gray-700 rounded-xl p-5 ${color}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-400">{title}</p>
        <p className="text-3xl font-bold text-white mt-1">{value.toLocaleString()}</p>
      </div>
      <div className="w-12 h-12 rounded-lg bg-gray-700/50 flex items-center justify-center">
        {icon}
      </div>
    </div>
  </div>
);

const EmailConnectionsPage: React.FC = () => {
  const contextData = useOutletContext<OutletContextType | undefined>();
  const themes = contextData?.themes ?? {
    dashboard: { from: '#6366f1', to: '#8b5cf6', glow: 'shadow-indigo-500/30' },
    companies: { from: '#2563eb', to: '#3b82f6', glow: 'shadow-blue-500/30' },
    onboard: { from: '#16a34a', to: '#22c55e', glow: 'shadow-green-500/30' },
    analytics: { from: '#0891b2', to: '#06b6d4', glow: 'shadow-cyan-500/30' },
    security: { from: '#db2777', to: '#f472b6', glow: 'shadow-pink-500/30' },
  };
  const theme = themes.onboard;

  const {
    connections,
    loading,
    error,
    refetch,
    triggerSync,
    isSyncing,
    stats,
  } = useEmailConnectionsAdmin();

  const [syncingCompanyId, setSyncingCompanyId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const handleTriggerSync = async (companyId: string, companyName?: string) => {
    setSyncingCompanyId(companyId);
    try {
      const results = await triggerSync(companyId);

      const totalFiles = results.reduce((sum, r) => sum + r.filesExtracted, 0);
      const hasErrors = results.some(r => r.errors.length > 0);

      if (hasErrors) {
        toast.error(`Sync completed with errors for ${companyName || companyId}`);
      } else if (totalFiles > 0) {
        toast.success(`Synced ${totalFiles} files for ${companyName || companyId}`);
      } else {
        toast.success(`No new files found for ${companyName || companyId}`);
      }
    } catch (err: any) {
      toast.error(err.message || 'Sync failed');
    } finally {
      setSyncingCompanyId(null);
    }
  };

  // Filter connections
  const filteredConnections = filterStatus === 'all'
    ? connections
    : connections.filter(c => c.status === filterStatus);

  // Group by company
  const connectionsByCompany = filteredConnections.reduce((acc, conn) => {
    if (!acc[conn.companyId]) {
      acc[conn.companyId] = {
        companyName: conn.companyName || conn.companyId,
        connections: [],
      };
    }
    acc[conn.companyId].connections.push(conn);
    return acc;
  }, {} as Record<string, { companyName: string; connections: EmailConnectionAdmin[] }>);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className="mb-6 p-6 rounded-2xl shadow-xl"
          style={{ background: `linear-gradient(135deg, ${theme.from}, ${theme.to})` }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail size={28} className="text-white" />
              <div>
                <h1 className="text-2xl font-bold text-white">Email Connections</h1>
                <p className="text-white/70 text-sm">Monitor OAuth-connected email accounts across all companies</p>
              </div>
            </div>
            <button
              onClick={() => refetch()}
              disabled={loading}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Connections"
            value={stats.totalConnections}
            icon={<Mail className="text-blue-400" size={24} />}
            color="hover:border-blue-500/50"
          />
          <StatCard
            title="Active Connections"
            value={stats.activeConnections}
            icon={<CheckCircle className="text-green-400" size={24} />}
            color="hover:border-green-500/50"
          />
          <StatCard
            title="Files Extracted"
            value={stats.totalFilesExtracted}
            icon={<FileText className="text-purple-400" size={24} />}
            color="hover:border-purple-500/50"
          />
          <StatCard
            title="Companies"
            value={stats.companiesWithConnections}
            icon={<Building2 className="text-cyan-400" size={24} />}
            color="hover:border-cyan-500/50"
          />
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-gray-400">Filter:</span>
          {['all', 'active', 'error', 'pending_auth'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                filterStatus === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {status === 'all' ? 'All' : status.replace('_', ' ')}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg flex items-center gap-2">
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="animate-spin text-gray-400" size={32} />
          </div>
        ) : filteredConnections.length === 0 ? (
          <div className="text-center p-12 bg-gray-800/30 border border-gray-700 rounded-xl">
            <Mail className="mx-auto text-gray-500 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-300 mb-2">No email connections found</h3>
            <p className="text-gray-500">
              {filterStatus !== 'all' ? 'Try a different filter.' : 'Companies will appear here once they connect their email.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(connectionsByCompany).map(([companyId, { companyName, connections: companyConnections }]) => (
              <div key={companyId} className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden">
                {/* Company Header */}
                <div className="px-5 py-3 bg-gray-800/80 border-b border-gray-700 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="text-gray-400" size={18} />
                    <span className="font-medium text-white">{companyName}</span>
                    <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded">
                      {companyConnections.length} connection{companyConnections.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <button
                    onClick={() => handleTriggerSync(companyId, companyName)}
                    disabled={isSyncing || syncingCompanyId === companyId}
                    className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 transition-colors flex items-center gap-1"
                  >
                    {syncingCompanyId === companyId ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Play size={14} />
                    )}
                    Sync Now
                  </button>
                </div>

                {/* Connections Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-900/50">
                      <tr className="text-left text-xs text-gray-400">
                        <th className="px-5 py-3">Email</th>
                        <th className="px-5 py-3">Type</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3">Last Sync</th>
                        <th className="px-5 py-3">Result</th>
                        <th className="px-5 py-3 text-right">Files</th>
                        <th className="px-5 py-3 text-right">RCTIs</th>
                        <th className="px-5 py-3 text-right">Messages</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {companyConnections.map((conn) => (
                        <tr key={conn.connectionId} className="hover:bg-gray-800/30">
                          <td className="px-5 py-3">
                            <span className="text-white">{conn.userEmail}</span>
                          </td>
                          <td className="px-5 py-3">
                            <span className="text-gray-400">{providerLabel(conn.type)}</span>
                            {conn.providerTenantId && (
                              <div className="text-xs text-gray-500 font-mono" title="Microsoft directory (tenant) id">
                                {conn.providerTenantId}
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <StatusBadge status={conn.status} />
                          </td>
                          <td className="px-5 py-3 text-sm text-gray-400">
                            {conn.lastSyncAt
                              ? conn.lastSyncAt.toLocaleString()
                              : 'Never'}
                          </td>
                          <td className="px-5 py-3">
                            <SyncResultBadge result={conn.lastSyncResult} />
                            {(conn.lastSyncDuplicatesSkipped || 0) > 0 && (
                              <div
                                className="text-xs text-gray-500 mt-1"
                                title="Files already loaded, skipped so nothing is counted twice"
                              >
                                {conn.lastSyncDuplicatesSkipped} already loaded, skipped
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <span className="text-white font-medium">{conn.filesExtracted}</span>
                          </td>
                          <td className="px-5 py-3 text-right">
                            <span className="text-amber-400 font-medium">{conn.rctiStored || 0}</span>
                            {(conn.rctiPaired || 0) > 0 && (
                              <span className="text-xs text-gray-500 ml-1">({conn.rctiPaired} paired)</span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <span className="text-gray-400">{conn.messagesProcessed}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Error Details */}
                {companyConnections.some(c => c.lastSyncError) && (
                  <div className="px-5 py-3 bg-red-900/20 border-t border-red-500/20">
                    {companyConnections
                      .filter(c => c.lastSyncError)
                      .map(c => (
                        <p key={c.connectionId} className="text-xs text-red-400">
                          <strong>{c.userEmail}:</strong> {c.lastSyncError}
                        </p>
                      ))}
                  </div>
                )}

                {/* Auth Error Details (Microsoft OAuth exchange / refresh) */}
                {companyConnections.some(c => c.lastAuthError) && (
                  <div className="px-5 py-3 bg-amber-900/20 border-t border-amber-500/20">
                    {companyConnections
                      .filter(c => c.lastAuthError)
                      .map(c => (
                        <p key={c.connectionId} className="text-xs text-amber-400">
                          <strong>{c.userEmail}</strong> auth: {c.lastAuthError}
                        </p>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailConnectionsPage;
